import React from 'react';

import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import ArrowRight from 'components/icons/arrow-right';
import { ServiceMode } from 'state/order';
import { useStoreContext } from 'state/store';

import { ModalContent, ModalHeading } from './styled';
import { OnServiceModeSelected } from './types';

interface IModalContentProps {
  selectServiceMode: OnServiceModeSelected;
}

const disabledCss = css`
  color: ${Styles.color.grey.three};

  &&:hover {
    background: transparent;
  }

  h4 {
    color: ${Styles.color.grey.three};
  }
`;

const Option = styled.button`
  /* button reset */
  border: 0;
  background: transparent;
  color: ${Styles.color.secondary};
  font: inherit;
  font-size: 1rem;
  margin: 0;
  padding: 0 1rem;
  text-align: left;
  width: 100%;

  ${p => p.disabled && disabledCss}

  align-items: center;
  border-bottom: 2px solid ${Styles.color.grey.four};
  display: flex;
  justify-content: space-between;

  &:hover {
    background: ${Styles.color.grey.five};
  }

  svg {
    fill: ${Styles.color.grey.one};
    flex-shrink: 0;
    margin-left: 1rem;
    width: 0.5rem;
  }
`;

const OptionContent = styled.div`
  /* add padding to content to center arrow within entire container */
  padding-bottom: 1.5rem;
`;

const Heading = styled.h4`
  color: ${Styles.color.primary};
  margin-bottom: 0;
`;

const PickupModeContent: React.FC<IModalContentProps> = props => {
  const { serviceModeStatus } = useStoreContext();
  const { formatMessage } = useIntl();

  const dineInAvailable = serviceModeStatus.EAT_IN.available;
  const takeoutAvailable = serviceModeStatus.TAKEOUT.available;
  const driveThruOpen = serviceModeStatus.DRIVE_THRU.available;
  const curbsideAvailable = serviceModeStatus.CURBSIDE.available;

  const disableDineIn = serviceModeStatus.EAT_IN.disabled;
  const disableTakeOut = serviceModeStatus.TAKEOUT.disabled;
  const disableDriveThru = serviceModeStatus.DRIVE_THRU.disabled;
  const disableCurbside = serviceModeStatus.CURBSIDE.disabled;

  return (
    <ModalContent>
      <ModalHeading id="modal-heading">
        {formatMessage({ id: 'pickupModeContentHeading' })}
      </ModalHeading>
      <>
        {takeoutAvailable && (
          <Option
            disabled={disableTakeOut}
            data-testid="pickupmode-take-out"
            onClick={() => props.selectServiceMode(ServiceMode.TAKEOUT)}
          >
            <OptionContent>
              <Heading>{formatMessage({ id: 'pickupModeTakeoutHeading' })}</Heading>
              <div>
                {formatMessage(
                  disableTakeOut ? { id: 'takeOutUnavailable' } : { id: 'pickupModeTakeoutContent' }
                )}
              </div>
            </OptionContent>
            <ArrowRight />
          </Option>
        )}
        {dineInAvailable && (
          <Option
            disabled={disableDineIn}
            data-testid="pickupmode-dine-in"
            onClick={() => props.selectServiceMode(ServiceMode.EAT_IN)}
          >
            <OptionContent>
              <Heading>{formatMessage({ id: 'pickupModeDineInHeading' })}</Heading>
              <div>
                {formatMessage(
                  disableDineIn ? { id: 'dineInUnavailable' } : { id: 'pickupModeDineInContent' }
                )}
              </div>
            </OptionContent>
            <ArrowRight />
          </Option>
        )}
      </>
      {curbsideAvailable && (
        <Option
          disabled={disableCurbside}
          data-testid="pickupmode-curbside"
          onClick={() => props.selectServiceMode(ServiceMode.CURBSIDE)}
        >
          <OptionContent>
            <Heading>{formatMessage({ id: 'pickupModeCurbsideHeading' })}</Heading>
            <div>
              {formatMessage(
                disableCurbside
                  ? { id: 'curbsideUnavailable' }
                  : { id: 'pickupModeCurbsideContent' }
              )}
            </div>
          </OptionContent>
          <ArrowRight />
        </Option>
      )}
      {driveThruOpen && (
        <Option
          disabled={disableDriveThru}
          data-testid="pickupmode-drive-thru"
          onClick={() => props.selectServiceMode(ServiceMode.DRIVE_THRU)}
        >
          <OptionContent>
            <Heading>{formatMessage({ id: 'pickupModeDriveThruHeading' })}</Heading>
            <div>
              {formatMessage(
                disableDriveThru
                  ? { id: 'driveThruUnavailable' }
                  : { id: 'pickupModeDriveThruContent' }
              )}
            </div>
          </OptionContent>
          <ArrowRight />
        </Option>
      )}
    </ModalContent>
  );
};

export default PickupModeContent;
